import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styles from "./Section2.module.css";
import BetSlip from "../betslip/BetSlip";
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from "../../../../config/api";
import ViewMoreBetsModal from "../../../../components/viewMoreBetsModal";
import LiveTVModal from "../../../../components/liveTVModal";
import { getSportsScoreCard } from "../../../../services/api";
import { Tv } from "lucide-react";

export default function Section2({ selectedBet, onCloseBet, onBetSubmit }) {
  const [activeTab, setActiveTab] = useState('Matched'); // 'Matched' or 'Unmatched'
  const [matchedBets, setMatchedBets] = useState([]);
  const [unmatchedBets, setUnmatchedBets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [showLiveTV, setShowLiveTV] = useState(false);
  const [tvUrl, setTvUrl] = useState(null);

  const token = localStorage.getItem("ownerToken") || localStorage.getItem("staffToken") || localStorage.getItem("token");
  const { sid, gmid } = useParams();

  // Handlers to open windows
  const handleViewMore = () => {
    setShowViewMoreModal(true);
  };

  // Fetch matched bets (Now fetching Open Bets as per requirement)
  const fetchMatchedBets = useCallback(async () => {
    if (!token || !gmid) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/user/cricket/open-bets/all?eventid=${gmid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const bets = response?.data?.data || [];
      // Response from open-bets/all is already filtered by eventid usually, but keeping safeguard if needed or just setting directly
      setMatchedBets(bets);
    } catch (err) {
      console.error("Failed to fetch matched bets:", err);
    } finally {
      setLoading(false);
    }
  }, [token, gmid]);

  // Fetch unmatched bets (Disabled as per requirement)
  const fetchUnmatchedBets = useCallback(async () => {
    // No API call for Unmatched tab
    setUnmatchedBets([]);
  }, []);

  // Fetch TV URL
  useEffect(() => {
    const fetchTv = async () => {
      if (gmid && sid) {
        try {
          const url = await getSportsScoreCard(gmid, sid);
          setTvUrl(url);
        } catch (e) {
          console.error("Error fetching TV:", e);
        }
      }
    };
    fetchTv();
  }, [gmid, sid]);

  useEffect(() => {
    if (gmid) {
      if (activeTab === 'Matched') {
        fetchMatchedBets();
      }
    }
    // Set interval to refresh bets
    const interval = setInterval(() => {
      if (gmid && activeTab === 'Matched') {
        fetchMatchedBets();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [gmid, activeTab, fetchMatchedBets]);

  const currentBets = activeTab === 'Matched' ? matchedBets : unmatchedBets;

  return (
    <div className={styles.section2}>
      {/* BetSlip Overlay */}
      {selectedBet && (
        <BetSlip
          bet={selectedBet}
          onClose={onCloseBet}
          isMobile={false}
          onBetSubmit={onBetSubmit}
        />
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'Matched' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('Matched')}
          >
            Matched
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'Unmatched' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('Unmatched')}
          >
            Unmatched
          </button>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.viewMoreBtn} onClick={handleViewMore}>
            View More
          </button>
          <button className={styles.liveTvBtn} onClick={() => setShowLiveTV(true)}>
            <Tv size={16} /> Live TV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th>UserName</th>
              <th>Mname</th>
              <th>Nation</th>
              <th>Type</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {currentBets.length > 0 ? (
              currentBets.map((bet, idx) => {
                const isBack = bet.type === 'BACK' || bet.bet_type === 'back' || bet.is_back === true || bet.bet_type === 'yes' || bet.bet_type === 'Yes';
                return (
                  <tr key={idx} className={isBack ? styles.rowBack : styles.rowLay}>
                    <td>{bet.username || bet.user_name || "Self"}</td>
                    <td>{bet.game_type || bet.marketName || "match_odds"}</td>
                    <td>{bet.selection_name || bet.team_name}</td>
                    <td className={isBack ? styles.typeBack : styles.typeLay}>
                      {(bet.type || bet.bet_type || (bet.is_back ? 'back' : 'lay')).toLowerCase()}
                    </td>
                    <td>{bet.odds || bet.user_rate}</td>
                    <td>{bet.stake || bet.amount || bet.stake_amount}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className={styles.noData}>
                  No {activeTab.toLowerCase()} bets
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Components */}
      {showViewMoreModal && (
        <ViewMoreBetsModal
          onClose={() => setShowViewMoreModal(false)}
          matchId={gmid}
          initialTab={activeTab}
        />
      )}

      {showLiveTV && (
        <LiveTVModal
          url={tvUrl}
          onClose={() => setShowLiveTV(false)}
        />
      )}
    </div>
  );
}
